import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { Account } from 'types'
import { useIdentity } from '@/hooks/auth/useIdentity'
import {
  unseenReleases,
  visibleReleases,
  type Release,
} from '@/routes/changelog/data'

import { useChangelogLastSeen } from './useChangelogSeen'

interface ChangelogModalContextValue {
  /** Whether the welcome modal is currently open. */
  open: boolean
  /**
   * Whether there are releases this account has not been shown yet that carry
   * at least one entry for its role. Drives both the auto-open and the sider
   * "gift" badge.
   */
  hasUpdates: boolean
  /**
   * The releases the modal presents, newest first: the unseen ones when there
   * are any, else the newest release the role has an entry in (the page's
   * "Voir le récap'" button).
   */
  shownReleases: Release[]
  /** The account's role, as the changelog filters on it. */
  role: string | undefined
  openModal: () => void
  /** Closes the modal and records that the account has seen every release. */
  closeModal: () => void
}

// Safe defaults so consumers rendered outside the provider (e.g. isolated
// component tests of the Sider) keep working without a wrapper.
const ChangelogModalContext = createContext<ChangelogModalContextValue>({
  open: false,
  hasUpdates: false,
  shownReleases: [],
  role: undefined,
  openModal: () => {},
  closeModal: () => {},
})

export const ChangelogModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: identity } = useIdentity<Account>()
  const role = identity?.account_type ?? undefined
  const { lastSeen, markSeen } = useChangelogLastSeen(identity?.id)
  const [open, setOpen] = useState(false)
  const [shownReleases, setShownReleases] = useState<Release[]>([])
  const didAutoOpen = useRef(false)

  // Only releases with an entry for this role count; one that concerns other
  // account types never opens the modal nor lights the badge for this one.
  const pending = useMemo(
    () => unseenReleases(role, lastSeen),
    [role, lastSeen],
  )
  const hasUpdates = pending.length > 0

  const openModal = useCallback(() => {
    const latest = visibleReleases(role)[0]
    setShownReleases(pending.length > 0 ? pending : latest ? [latest] : [])
    setOpen(true)
  }, [pending, role])

  // Auto-open exactly once when this account has unseen releases for its role.
  useEffect(() => {
    if (hasUpdates && !didAutoOpen.current) {
      didAutoOpen.current = true
      openModal()
    }
  }, [hasUpdates, openModal])

  const value = useMemo<ChangelogModalContextValue>(
    () => ({
      open,
      hasUpdates,
      shownReleases,
      role,
      openModal,
      closeModal: () => {
        setOpen(false)
        markSeen()
      },
    }),
    [open, hasUpdates, shownReleases, role, openModal, markSeen],
  )

  return (
    <ChangelogModalContext.Provider value={value}>
      {children}
    </ChangelogModalContext.Provider>
  )
}

export const useChangelogModal = (): ChangelogModalContextValue =>
  useContext(ChangelogModalContext)
